import { faker } from '@faker-js/faker/locale/ar';
import { LocalDate } from '@js-joda/core';
import type { SignupDTOTypes } from '../types';
import { SignupRequestDTO } from '../types/signup';

type AlternateDeliveryLocation = SignupRequestDTO['delivery']['alternateDeliveryLocation'];
type CustomerAddress = SignupRequestDTO['customer']['address'];

export class SignupRequestDTOBuilder {
  private deliveryDate: LocalDate = LocalDate.parse('2025-01-01');
  private availableRecipeIds: string[] = [
    '11111111-1111-1111-1111-111111111111',
    '22222222-2222-2222-2222-222222222222',
  ];
  private emailAddress: string = faker.internet.email();
  private password: string = 'password';
  private signupPromoCode?: string;
  private privacyPolicyVersion: string = faker.string.alphanumeric({ length: 40 });
  private marketingConsentOptionsVersion: string = faker.string.alphanumeric({ length: 40 });
  private affiliateDetails: { clickSourceId: string; channel: string } | undefined;
  private firstName = faker.person.firstName();
  private lastName = faker.person.lastName();
  private phoneNumber = '0871234567';
  private address: CustomerAddress = {
    line1: 'line1',
    line2: 'line2',
    city: 'city',
    postalCode: 'D01AB12',
    province: 'province',
    country: 'country',
  };
  private numberOfPortions: number = 2;
  private alternateDeliveryLocation: AlternateDeliveryLocation = {
    choice: 'garage',
  };
  private clientId?: string = '1125799204.1769507086';
  private singleUsePaymentTokenOverride?: string;

  public build(): SignupDTOTypes.SignupRequestDTO {
    return {
      numberOfPortions: this.numberOfPortions,
      delivery: {
        date: this.deliveryDate.toString(),
        alternateDeliveryLocation: this.alternateDeliveryLocation,
      },
      recipeIds: this.availableRecipeIds,
      customer: {
        firstName: this.firstName,
        lastName: this.lastName,
        phoneNumber: this.phoneNumber,
        address: this.address,
        email: this.emailAddress,
        password: this.password,
        marketingConsent: {
          source: 'signup-web-ie' as const,
          privacyPolicyVersion: this.privacyPolicyVersion,
          choices: [
            {
              purpose: 'internal-marketing',
              version: this.marketingConsentOptionsVersion,
              consents: [
                {
                  type: 'email',
                  consented: true,
                },
                {
                  type: 'sms',
                  consented: true,
                },
                {
                  type: 'phone',
                  consented: true,
                },
                {
                  type: 'post',
                  consented: true,
                },
                {
                  type: 'push_notification',
                  consented: true,
                },
              ],
            },
            {
              purpose: 'third-party-marketing',
              version: this.marketingConsentOptionsVersion,
              consents: [
                {
                  type: 'third-party',
                  consented: true,
                },
              ],
            },
          ],
        },
      },
      promoCode: this.signupPromoCode,
      singleUsePaymentToken: this.singleUsePaymentTokenOverride ?? faker.string.uuid(),
      affiliateDetails: this.affiliateDetails,
      clientId: this.clientId,
    };
  }

  public withNumberOfPortions(numberOfPortions: number) {
    this.numberOfPortions = numberOfPortions;
    return this;
  }

  public withRecipeIds(availableRecipeIds: string[]) {
    this.availableRecipeIds = availableRecipeIds;
    return this;
  }

  public withDeliveryDate(openDeliveryDate: LocalDate) {
    this.deliveryDate = openDeliveryDate;
    return this;
  }

  public withPromoCode(signupPromoCode: string) {
    this.signupPromoCode = signupPromoCode;
    return this;
  }

  public withPrivacyPolicyVersion(privacyPolicyVersion: string) {
    this.privacyPolicyVersion = privacyPolicyVersion;
    return this;
  }

  public withPassword(password: string) {
    this.password = password;
    return this;
  }

  public withEmailAddress(emailAddress: string) {
    this.emailAddress = emailAddress;
    return this;
  }

  public withAddress(address: Partial<CustomerAddress>) {
    this.address = { ...this.address, ...address };
    return this;
  }

  public withMarketingConsentOptionsVersion(marketingConsentOptionsVersion: string) {
    this.marketingConsentOptionsVersion = marketingConsentOptionsVersion;
    return this;
  }

  public withAlternateDeliveryLocation(alternateDeliveryLocation: AlternateDeliveryLocation) {
    this.alternateDeliveryLocation = alternateDeliveryLocation;
    return this;
  }

  public withAffiliateDetails(affiliateDetails?: { clickSourceId: string; channel: string }) {
    this.affiliateDetails = affiliateDetails;
    return this;
  }

  public withFirstName(firstName: string) {
    this.firstName = firstName;
    return this;
  }

  public withLastName(lastName: string) {
    this.lastName = lastName;
    return this;
  }

  public withPhoneNumber(phoneNumber: string) {
    this.phoneNumber = phoneNumber;
    return this;
  }

  public withClientId(clientId?: string) {
    this.clientId = clientId;
    return this;
  }

  public withSingleUsePaymentToken(token: string) {
    this.singleUsePaymentTokenOverride = token;
    return this;
  }
}
